/**
 * EmptyStateContainer Component
 *
 * A reusable wrapper component that adds the decorative blob SVG backdrop
 * to any empty state content. This provides visual consistency across all
 * empty states in the application.
 *
 * @example
 * ```tsx
 * <EmptyStateContainer size="md">
 *   <BlockStack gap="200" inlineAlign="center">
 *     <Icon source={SearchIcon} />
 *     <Text>No results found</Text>
 *   </BlockStack>
 * </EmptyStateContainer>
 * ```
 */

import type { ReactNode } from 'react';

export interface EmptyStateContainerProps {
  /** Content to render inside the backdrop container */
  children: ReactNode;
  /** Size variant affecting height and backdrop scale */
  size?: 'sm' | 'md' | 'lg';
  /** Additional CSS class names */
  className?: string;
}

/**
 * Container component that provides the decorative blob SVG backdrop
 * for empty state displays throughout the application.
 */
export function EmptyStateContainer({
  children,
  size = 'md',
  className,
}: EmptyStateContainerProps) {
  const classes = [
    'empty-state-backdrop',
    `empty-state-backdrop--${size}`,
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={classes}>
      {children}
    </div>
  );
}
